import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen, Award } from "lucide-react";

export default function EducationNotes() {
  const [notes, setNotes] = useState([]);
  const [eleves, setEleves] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());

  function empty() {
    return { eleve_id: "", matiere: "", libelle: "", note: 0, sur: 20, coefficient: 1, date_evaluation: new Date().toISOString().slice(0, 10), commentaire: "" };
  }

  const load = async () => {
    try {
      const [n, e] = await Promise.all([api.get("/chef/education/notes"), api.get("/chef/education/eleves")]);
      setNotes(n.data); setEleves(e.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/chef/education/notes", { ...form, note: parseFloat(form.note), sur: parseFloat(form.sur), coefficient: parseFloat(form.coefficient) });
      toast.success("Note ajoutée");
      setOpen(false); setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try { await api.delete(`/chef/education/notes/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = filter === "all" ? notes : notes.filter((n) => n.eleve_id === filter);
  const eleveById = (id) => eleves.find((e) => e.id === id);

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Éducation · Notes"
        title="Carnet de notes"
        sub="Saisissez et consultez les évaluations par élève et matière."
        right={
          <div className="flex gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="notes-filter" className="h-11 w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les élèves</SelectItem>
                {eleves.map((e) => <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="note-add-btn" className="h-11 bg-[#002FA7] text-white"><Plus className="h-4 w-4 mr-2" /> Saisir une note</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Nouvelle note</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="note-form">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Élève</Label>
                    <Select value={form.eleve_id} onValueChange={(v) => setForm({ ...form, eleve_id: v })}>
                      <SelectTrigger className="h-11 mt-1" data-testid="note-eleve"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                      <SelectContent>
                        {eleves.map((e) => <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Matière</Label>
                      <Input value={form.matiere} onChange={(e) => setForm({ ...form, matiere: e.target.value })} required className="h-11 mt-1" data-testid="note-matiere" placeholder="Maths, Français…" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Libellé</Label>
                      <Input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} required className="h-11 mt-1" data-testid="note-libelle" placeholder="DS n°1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Note</Label>
                      <Input type="number" step="0.5" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} required className="h-11 mt-1" data-testid="note-valeur" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Sur</Label>
                      <Input type="number" step="0.5" value={form.sur} onChange={(e) => setForm({ ...form, sur: e.target.value })} required className="h-11 mt-1" data-testid="note-sur" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Coefficient</Label>
                      <Input type="number" step="0.1" value={form.coefficient} onChange={(e) => setForm({ ...form, coefficient: e.target.value })} required className="h-11 mt-1" data-testid="note-coef" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Date d'évaluation</Label>
                    <Input type="date" value={form.date_evaluation} onChange={(e) => setForm({ ...form, date_evaluation: e.target.value })} required className="h-11 mt-1" data-testid="note-date" />
                  </div>
                  <DialogFooter><Button type="submit" disabled={!form.eleve_id || !form.matiere} className="bg-[#002FA7] text-white" data-testid="note-submit">Enregistrer</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <div className="p-6 lg:p-10">
        <div className="bg-white border border-[#DEE2E6]" data-testid="notes-list">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]">
              <BookOpen className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />
              Aucune note. Cliquez sur "Saisir une note".
            </div>
          ) : filtered.map((n) => {
            const e = eleveById(n.eleve_id);
            const pct = (n.note / n.sur) * 100;
            const couleur = pct >= 70 ? "#059669" : pct >= 50 ? "#D97706" : "#E60000";
            return (
              <div key={n.id} className="p-4 border-b border-[#F8F9FA] last:border-b-0 grid grid-cols-12 gap-3 items-center hover:bg-[#F8F9FA]" data-testid={`note-${n.id}`}>
                <div className="col-span-3 font-bold">{e ? `${e.prenom} ${e.nom}` : "Élève supprimé"}</div>
                <div className="col-span-2 text-sm font-semibold text-[#002FA7]">{n.matiere}</div>
                <div className="col-span-3 text-sm text-[#495057]">{n.libelle}</div>
                <div className="col-span-2 font-mono">
                  <span className="font-black text-lg" style={{ color: couleur }}>{n.note}</span>
                  <span className="text-[#868E96]"> / {n.sur}</span>
                  <span className="text-[10px] text-[#868E96] ml-1">×{n.coefficient}</span>
                </div>
                <div className="col-span-1 text-[10px] text-[#868E96] font-mono">{n.date_evaluation ? new Date(n.date_evaluation).toLocaleDateString("fr-FR") : "—"}</div>
                <div className="col-span-1 flex justify-end">
                  <Button size="sm" variant="outline" onClick={() => remove(n.id)} data-testid={`note-delete-${n.id}`} className="h-7 px-2 border-[#E60000] text-[#E60000]"><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
